import React from "react";
import PhoneInput, { Value } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useColorMode,
  Select,
} from "@chakra-ui/react";
import { IconWrapper } from "./IconWrapper";
import { FiUser, FiMail, FiPhone, FiBriefcase, FiCalendar, FiCreditCard, FiLayers } from "react-icons/fi"; // Changed FiHash to FiCreditCard
import { db } from "../firebase/firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import CustomDropdown from "./CustomDropdown";
type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  assignedChannels?: Record<string, string[]>;
  assignedProjects?: string[];
  assignedTasks?: { id: string; name: string; channelId: string; projectId: string; taskId: string }[];
  organizationId: string;
  joiningDate?: string;
  employeeCode?: string;
  department?: string;
  address1?: string;
  address2?: string;
};

const AddUserModal: React.FC<{
  isUserSaving: boolean;
  bg: string;
  color: string;
  isOpen: boolean;
  onClose: () => void;
  newUser: any;
  setNewUser: (user: any) => void;
  setEditingUser: React.Dispatch<React.SetStateAction<User | null>>;
  editingUser: any;
  handleAddOrUpdateUser: () => void;
  organizationId: string;
}> = ({
  isOpen,
  onClose,
  newUser,
  setNewUser,
  bg,
  isUserSaving,
  color,
  setEditingUser,
  editingUser,
  handleAddOrUpdateUser,
  organizationId,
}) => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const [departments, setDepartments] = React.useState<{ id: string; name: string }[]>([]);

    React.useEffect(() => {
      const fetchDepartments = async () => {
        if (!organizationId) return;

        try {
          const q = query(
            collection(db, `organization/${organizationId}/departments`),
            orderBy("createdAt", "desc")
          );
          const querySnapshot = await getDocs(q);
          const deptList: { id: string; name: string }[] = [];
          querySnapshot.forEach((doc) => {
            deptList.push({ id: doc.id, name: doc.data().name });
          });
          setDepartments(deptList);
        } catch (error) {
          console.error("Error fetching departments:", error);
        }
      };

      if (isOpen) {
        fetchDepartments();
      }
    }, [isOpen, organizationId]);

    const handleCloseModal = () => {
      setNewUser({ firstName: "", lastName: "", email: "", phone: "", role: "", joiningDate: "", employeeCode: "", department: "", address1: "", address2: "" });
      setEditingUser(null);
      onClose();
    };
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        closeOnOverlayClick={false}
        closeOnEsc={true}
        size="2xl"
      >
        <ModalOverlay className={`${isDark ? "bg-[#0B0B0F]/75" : "bg-black/70"} backdrop-blur-[5px]`} />
        <ModalContent
          bg={isDark ? "#1A1C1F" : "white"}
          borderColor="#B59143"
          borderWidth="1px"
          className={`rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.55)] border max-w-4xl w-full`}
        >
          <ModalHeader color={color} className={`${isDark ? "text-white" : "text-gray-900"} text-xl font-semibold`}>
            {editingUser ? "Edit User" : "Add User"}
          </ModalHeader>
          <ModalCloseButton color={color} className={isDark ? "text-white" : undefined} />
          <ModalBody color={color} className={`${isDark ? "text-white" : "text-gray-700"}`} maxH="65vh" overflowY="auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormControl mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>First Name</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiUser} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <Input
                    value={newUser.firstName}
                    onChange={(e) =>
                      setNewUser({ ...newUser, firstName: e.target.value })
                    }
                    focusBorderColor="#B59143"
                    className={`${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white border-gray-300 text-gray-900"} border focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 rounded-lg`}
                  />
                </InputGroup>
              </FormControl>

              <FormControl mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Last Name</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiUser} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <Input
                    value={newUser.lastName}
                    onChange={(e) =>
                      setNewUser({ ...newUser, lastName: e.target.value })
                    }
                    focusBorderColor="#B59143"
                    className={`${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white border-gray-300 text-gray-900"} border hover:border-[#B59143] focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 rounded-lg`}
                  />
                </InputGroup>
              </FormControl>
              <FormControl className="md:col-span-2" mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Email</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiMail} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    focusBorderColor="#B59143"
                    className={`${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white border-gray-300 text-gray-900"} border hover:border-[#B59143] focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 rounded-lg`}
                  />
                </InputGroup>
              </FormControl>
              <FormControl className="md:col-span-2" mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Phone</FormLabel>
                <div className="relative">

                  <PhoneInput
                    defaultCountry="IN"
                    value={newUser.phone}
                    onChange={(phone: string | undefined) =>
                      setNewUser({ ...newUser, phone })
                    }
                    international
                    countryCallingCodeEditable={false}
                    className={`custom-phone-input ${isDark ? "dark" : ""}`}
                  />
                </div>
              </FormControl>
              <FormControl mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Role</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiBriefcase} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <Input
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    focusBorderColor="#B59143"
                    className={`${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white border-gray-300 text-gray-900"} border hover:border-[#B59143] focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 rounded-lg`}
                  />
                </InputGroup>
              </FormControl>
              <FormControl mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Joining Date</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiCalendar} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <Input
                    type="date"
                    value={newUser.joiningDate || ""}
                    onChange={(e) => setNewUser({ ...newUser, joiningDate: e.target.value })}
                    focusBorderColor="#B59143"
                    className={`${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white border-gray-300 text-gray-900"} border hover:border-[#B59143] focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 rounded-lg`}
                  />
                </InputGroup>
              </FormControl>
              <FormControl mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Employee Code</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiCreditCard} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <Input
                    value={newUser.employeeCode || ""}
                    onChange={(e) => setNewUser({ ...newUser, employeeCode: e.target.value })}
                    focusBorderColor="#B59143"
                    className={`${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white border-gray-300 text-gray-900"} border hover:border-[#B59143] focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 rounded-lg`}
                  />
                </InputGroup>
              </FormControl>
              <FormControl mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Department</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiLayers} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <CustomDropdown
                    options={departments.map((dept) => ({ value: dept.name, label: dept.name }))}
                    value={newUser.department || ""}
                    onChange={(val) => setNewUser({ ...newUser, department: val })}
                    isDark={isDark}
                    placeholder="Select Department"
                    className={`w-full ${isDark ? "bg-[#0B0B0F] border-[#2E2F33]" : "bg-white border-gray-300"} h-10`}
                    style={{ paddingLeft: '2.5rem' }}
                    placement="top"
                  />
                </InputGroup>
              </FormControl>
              <FormControl className="md:col-span-2" mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Address 1</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiBriefcase} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <Input
                    value={newUser.address1 || ""}
                    onChange={(e) => setNewUser({ ...newUser, address1: e.target.value })}
                    focusBorderColor="#B59143"
                    className={`${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white border-gray-300 text-gray-900"} border hover:border-[#B59143] focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 rounded-lg`}
                  />
                </InputGroup>
              </FormControl>
              <FormControl className="md:col-span-2" mb={0}>
                <FormLabel className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-700"}`}>Address 2</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <IconWrapper icon={FiBriefcase} className={`${isDark ? "text-[#C3C9D3]" : "text-gray-400"}`} size={18} />
                  </InputLeftElement>
                  <Input
                    value={newUser.address2 || ""}
                    onChange={(e) => setNewUser({ ...newUser, address2: e.target.value })}
                    focusBorderColor="#B59143"
                    className={`${isDark ? "bg-[#0B0B0F] text-white border-[#2E2F33]" : "bg-white border-gray-300 text-gray-900"} border hover:border-[#B59143] focus:border-[#B59143] focus:ring-2 focus:ring-[#B59143]/30 rounded-lg`}
                  />
                </InputGroup>
              </FormControl>
            </div>
          </ModalBody>
          <ModalFooter className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCloseModal}
              isDisabled={isUserSaving}
              className={`${isDark ? "border-[#2E2F33] text-white hover:bg-[#2A3441]" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
            >
              Cancel
            </Button>
            <Button
              isLoading={isUserSaving}
              loadingText="Saving..."
              variant="gold"
              onClick={handleAddOrUpdateUser}
              isDisabled={isUserSaving}
            >
              {editingUser ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

export default AddUserModal;
